<template>
  <div id="product-form" class="product-form mt-l" data-sign-post-location="main" js-sign-post>


    <div v-if="compatibilList != null" class="product-form__compatibility text-body-s-regular">
      {{ $string('product.sidebar.compatible_with') }}
      <span v-if="compatibilList.length > 1" v-for="compatible in compatibilList"
        class="main-product__compatibility-item text-heading-m" v-text="compatible" />
    </div>
    <div v-if="compatibilListSingle != null" class="product-form__compatibility text-body-s-regular">
      {{ $string('product.sidebar.compatible_with') }}

      <span v-if="compatibilListSingle" class="main-product__compatibility-item text-heading-m"
        v-text="compatibilListSingle" />

    </div>
    <form class="product-form__form" @submit.prevent="handleAddToCart">
      <div v-show="!product.hasOnlyDefaultVariant && productVariants.length > 0" class="product-form__variants grid">
        <h2 class="text-heading-m col xs6 l12" v-text="product.options[0].name" />

        <selection-tab v-for="(variantObject, index) of productVariants" :id="`variant-${variantObject.id}`"
          :key="`variant-${index}-${variantObject.id}`" v-model="currentVariantId" class="product-form__variant"
          :disabled="!variantObject.available" :label="getVariantTitle(variantObject)" :loading="loading"
          :price="variantObject.price" name="id" :value="variantObject.id" @click="handleTabClick(variantObject)" />
      </div>

      <quantity-selector v-if="quantitySelector" class="product-form__quantity-selector" large :min="1"
        :quantity="quantity" :state="state" @update-quantity="handleQuantityUpdate" />

      <input v-else name="quantity" readonly type="hidden" :value="quantity">

      <btn v-if="isTransactional && cartbtn==false" class="product-form__add-to-cart" :label="addToCartText" modifiers="fill block center"
        :show-state-icon="state === 'adding'" hover-only type="submit">
        <template #hoverIcon>
          <icon-cart />
        </template>
      </btn>
      <a v-if="cartbtn==true" class="button button--fill button--block button--center button--has-hover-icon"  :href="btnurl" v-text="btntext"></a>
    </form> 

    <notification-panel v-if="error" class-container="product-form__error" :show-close="false" :text="error"
      type="error" />
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Boolean} [quantitySelector] - Show quantity selector.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import NotificationPanel from '~async/utils/notification-panel/notification-panel'
import QuantitySelector from '~async/quantity-selector/quantity-selector'
import SelectionTab from '~async/utils/selection-tab/selection-tab'
import IconCart from '~icons/button/cart.svg'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    IconCart,
    NotificationPanel,
    QuantitySelector,
    SelectionTab,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    quantitySelector: {
      type: Boolean,
      default: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    cartbtn : {
      type: Boolean,
      default: false,
    },
    btntext : {
      type: [String],
      default: false,
    },
    btnurl : {
      type: [String],
      default: false,
    },
  },

  emits: {
    'update-variant': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      addedVariant: false,
      currentVariantId: this.variant?.id || 0,
      error: '',
      lineItemProperties: {},
      quantity: 1,
      state: 'ready',
      timeout: {},
      isTransactional: false,
      compatibilList: null,
      compatibilListSingle: null,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute product variants to iterate over.
     * @returns {Array}
     */
    productVariants() {
      if (this.loading) {
        return [false, false, false, false, false]
      }

      const variants = this.localizeProduct()
      return variants
    },
  },

  watch: {

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant.id === this.currentVariantId) {
        return
      }

      const variant = this.getVariantFromId(this.currentVariantId)

      if (!variant) {
        return
      }
      if (this.product.type !== 'sawmil' && variant.shipsIn != null || variant.shipsIn != '') {
        if (document.getElementsByClassName('varianttexting').length > 1) {
          document.getElementsByClassName('varianttexting')[0].textContent = variant.shipsIn;
        }
      }
      this.$emit('update-variant', variant)
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     */
    product() {
      this.getCurrentVariant()
      this.quantity = 1
    },
  },

  mounted() {

    if (window.cnvs.transactionalMarkets.includes(window.cnvs.store.country.toLowerCase())) {
      this.isTransactional = true
    }
    this.getCurrentVariant()
    this.bindEventListeners()
    if (this.getVariantFromId(this.currentVariantId).varCompatibleWith && this.getVariantFromId(this.currentVariantId).varCompatibleWith.includes(',')) {
      this.compatibilList = this.getVariantFromId(this.currentVariantId).varCompatibleWith.split(',')
      this.compatibilListSingle = null
    } else if (this.getVariantFromId(this.currentVariantId).varCompatibleWith && this.getVariantFromId(this.currentVariantId).varCompatibleWith.includes('|')) {
      this.compatibilList = this.getVariantFromId(this.currentVariantId).varCompatibleWith.split('|')
      this.compatibilListSingle = null
    }
    else {
      this.compatibilListSingle = this.getVariantFromId(this.currentVariantId).varCompatibleWith
      this.compatibilList = null
    }
    if (this.product.type !== 'sawmil' && this.getVariantFromId(this.currentVariantId).shipsIn != null || this.getVariantFromId(this.currentVariantId).shipsIn != '') {
      if (document.getElementsByClassName('varianttexting').length > 1) {
        document.getElementsByClassName('varianttexting')[0].textContent = this.getVariantFromId(this.currentVariantId).shipsIn;
      }
    }
  },

  methods: {

    /**
     * Bind Event listeners
     */
    bindEventListeners() {
      cnvs.EventBus.listen('add-to-cart', () => {
        this.handleAddToCart()
      })
    },
    handleTabClick(variant) {

      if (variant.varCompatibleWith && variant.varCompatibleWith.includes(',')) {
        this.compatibilList = variant.varCompatibleWith != null ? variant.varCompatibleWith.split(',') : null
        this.compatibilListSingle = null
      } else if (variant.varCompatibleWith && variant.varCompatibleWith.includes('|')) {
        this.compatibilList = variant.varCompatibleWith != null ? variant.varCompatibleWith.split('|') : null
        this.compatibilListSingle = null
      } else {
        this.compatibilListSingle = variant.varCompatibleWith
        this.compatibilList = null
      }
      if (variant.shipsIn != null || variant.shipsIn != '' && document.getElementsByClassName('varianttexting').length > 0) {
        document.getElementsByClassName('varianttexting')[0].textContent = variant.shipsIn;

      }

      if (variant.shipsIn != null || variant.shipsIn != '') {

        document.getElementsByClassName('variant_usp_img')[0].style.display = 'block'
      }
      if (variant.shipsIn == null || variant.shipsIn == '') {

        document.getElementsByClassName('variant_usp_img')[0].style.display = 'none'
      }

    },
    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (!this.product || !this.product?.variants?.length) {
        return
      }

      if (this.product.hasOnlyDefaultVariant) {
        this.currentVariantId = this.product.variants[0].id
        return
      }

      /**
       * Pick the first in stock variant.
       * - If no variants are in stock use first.
       * - Or use variant from URL if set.
       */
      const inStockVariants = this.product.variants.filter((variant) => {
        return variant.available
      })

      const defaultVariant = inStockVariants.length
        ? inStockVariants[0]
        : this.product.variants[0]

      const variant = this.getVariantFromUrl() || defaultVariant

      this.currentVariantId = variant.id
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))
      return this.getVariantFromId(variantId)
    },

    /**
     * Get variant object from variant ID.
     * @returns {Object}
     */
    getVariantFromId(variantId) {
      return this.product.variants.find((variant) => {
        return variant.id === variantId
      })
    },

    /**
     * Get variant title.
     * @param {Object} variant - Variant object.
     * @returns {String}
     */
    getVariantTitle(variant) {
      if (!variant) {
        return this.$string('accessibility.loading')
      }

      return variant.available
        ? variant.title
        : `${variant.title} <span class="visually-hidden">${this.$string('product.form.sold_out')}</span>`
    },

    /**
     * Handle quantity selector update.
     * @param {String} quantity - New quantity.
     */
    handleQuantityUpdate(quantity) {
      this.quantity = quantity
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.variant) {
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (cnvs.storybook) {
        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, this.$timing('slow'))

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

        return
      }

      let items = []
          try {
            console.log('this.product.giftproducts',this.product)
            if (this.product.giftproducts != "" && this.product.giftproducts.length > 0) {
              let giftItems = this.product.giftproducts[0];
              items = [
                {
                  id: this.variant.id,
                  properties: this.lineItemProperties,
                  quantity: this.quantity,
                },
                {
                  id: giftItems.selectedOrFirstAvailableVariant.id,
                  quantity: 1,
                  properties: {
                    'Gift': 'Gift items'
                  }
                },
              ]
              
            } else {
              items = [
                {
                  id: this.variant.id,
                  properties: this.lineItemProperties,
                  quantity: this.quantity,
                },

              ]
            
            }
//          items = [
//            {
//              id: this.variant.id,
//              properties: this.lineItemProperties,
//              quantity: this.quantity,
//            },
//
//          ]
        await this.addItem(items)
        this.addedVariant = this.variant
        this.state = 'added'

        if (
          cnvs.settings.autoOpenCartDrawer &&
          this.getComponentExists('cart-drawer')
        ) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        await this.resetState()

      } catch (error) {
        this.state = 'error'

        if (error?.description) {
          this.error = error.description
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'))
      })

      this.state = 'ready'

      /**
       * If user has already changed variant then don't reset form.
       */
      if (this.variant !== this.addedVariant) {
        this.addedVariant = false
        return
      }

      this.quantity = 1

      /**
       * Revert to first available variant.
       * - If there isn't one (product out of stock) then select first variant.
       */
      let firstAvailableVariant = this.product.variants.find((variant) => {
        return variant.available
      })

      if (!firstAvailableVariant) {
        firstAvailableVariant = this.product.variants[0]
      }

      this.currentVariantId = firstAvailableVariant.id
      this.addedVariant = false
    },

    /**
     * Check product variants to see if any are exclusive to a separate market
     */
    localizeProduct() {
      const localizedVariants = []

      this.product.variants.forEach((el) => {
        if (!el?.exclusiveMarket) {
          localizedVariants.push(el)
        }

        if (el?.exclusiveMarket && this.isPartOfLocale(el)) {
          localizedVariants.push(el)
        }
      })

      return localizedVariants
    },

    /**
     * Check if an accordion is active.
     * @param {Object} product - product object.
     * check if product has exclusiveMarket param and it matches current locale
     */
    isPartOfLocale(product) {
      return product.exclusiveMarket.includes(window.cnvs.store.country.toLowerCase())
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
</style>
